import type { ScenarioDef } from "../data/scenario";
import type { MissionDef } from "./types";
import { GIDEON_MISSIONS } from "./gideon/missions";
import { N1, N2, N3, N4, N5, N6, N7, N8 } from "./lieSkins";
import {
  STORY_BEFORE_THE_KING,
  STORY_JOY_OF_THE_LORD,
  STORY_MOCKERY,
  STORY_NIGHT_SURVEY,
  STORY_RISE_AND_BUILD,
  STORY_SCHEMES,
  STORY_STRENGTH_DECAYED,
  STORY_SWORD_AND_TROWEL,
} from "./story";

import { STARTER_OWNED_IDS } from "../../../shared/collection";

/**
 * Campaign start owns the full Mission 1 teaching loadout.
 * Fixed-hand missions may still *borrow* unowned cards later;
 * rewards should be cards not already in this set so grants feel real.
 * Shared with endless via `STARTER_OWNED_IDS`.
 */
export const CAMPAIGN_STARTER_OWNED: readonly string[] = STARTER_OWNED_IDS;

/** N1 — Fear / Mind only (reuse First Whispers combat). */
const BEFORE_THE_KING: ScenarioDef = {
  id: "neh-before-the-king",
  name: "Before the King",
  duration: 100,
  handSize: 5,
  maxIntegrity: 130,
  disableConfusion: true,
  spawns: [
    { at: 5, lieId: "whisper", label: "Fear before the throne", skin: N1.loseEverything },
    { at: 18, lieId: "whisper", skin: N1.onlyAServant },
    { at: 32, lieId: "whisper", label: "What if he refuses?", skin: N1.heWillRefuse },
    { at: 48, lieId: "whisper", skin: N1.loseEverything },
    { at: 64, lieId: "whisper", label: "Speak anyway", skin: N1.onlyAServant },
    { at: 80, lieId: "whisper", skin: N1.heWillRefuse },
    { at: 94, lieId: "whisper", label: "Last doubt", skin: N1.loseEverything },
  ],
};

/** N2 — Quiet honesty; sparse Fear (new script between M1 and M2 density). */
const NIGHT_SURVEY: ScenarioDef = {
  id: "neh-night-survey",
  name: "The Night Survey",
  duration: 95,
  handSize: 5,
  maxIntegrity: 125,
  disableConfusion: true,
  spawns: [
    { at: 8, lieId: "whisper", label: "Don't look", skin: N2.dontLook },
    { at: 24, lieId: "whisper", label: "Broken gates", skin: N2.brokenGates },
    { at: 42, lieId: "whisper", skin: N2.dontLook },
    { at: 58, lieId: "whisper", label: "Measure the ruin", skin: N2.brokenGates },
    { at: 74, lieId: "whisper", skin: N2.dontLook },
    { at: 88, lieId: "heavy-gloom", label: "The size of it", skin: N2.sizeOfIt },
  ],
};

/** N3 — Introduce Despair (reuse Gloom Settles). */
const RISE_AND_BUILD: ScenarioDef = {
  id: "neh-rise-and-build",
  name: "Rise and Build",
  duration: 100,
  handSize: 5,
  maxIntegrity: 95,
  spawns: [
    { at: 3, lieId: "whisper", label: "Old habits", skin: N3.startAndFail },
    { at: 10, lieId: "whisper", skin: N3.theyWillLaugh },
    { at: 18, lieId: "heavy-gloom", label: "Too heavy", skin: N3.tooHeavy },
    { at: 26, lieId: "whisper", skin: N3.startAndFail },
    { at: 34, lieId: "heavy-gloom", skin: N3.tooHeavy },
    { at: 42, lieId: "whisper", label: "They will laugh", skin: N3.theyWillLaugh },
    { at: 50, lieId: "heavy-gloom", skin: N3.tooHeavy },
    { at: 58, lieId: "whisper", skin: N3.startAndFail },
    { at: 66, lieId: "heavy-gloom", label: "Rise anyway", skin: N3.tooHeavy },
    { at: 74, lieId: "whisper", skin: N3.theyWillLaugh },
    { at: 82, lieId: "heavy-gloom", skin: N3.tooHeavy },
    { at: 90, lieId: "whisper", label: "Last push", skin: N3.startAndFail },
  ],
};

/** N4 — Heart spikes (reuse Bypass Thought). */
const MOCKERY: ScenarioDef = {
  id: "neh-mockery",
  name: "Mockery from Outside",
  duration: 105,
  handSize: 5,
  maxIntegrity: 90,
  spawns: [
    { at: 3, lieId: "whisper", label: "Mind opens", skin: N4.weakHands },
    { at: 10, lieId: "whisper", skin: N4.theyMockYou },
    { at: 18, lieId: "whisper", layer: "heart", label: "They mock you", skin: N4.theyMockYou },
    { at: 26, lieId: "heavy-gloom", skin: N3.tooHeavy },
    { at: 34, lieId: "whisper", layer: "heart", skin: N4.foxOnWall },
    { at: 42, lieId: "whisper", label: "Two fronts", skin: N4.weakHands },
    { at: 50, lieId: "heavy-gloom", layer: "heart", skin: N4.theyMockYou },
    { at: 58, lieId: "whisper", skin: N4.foxOnWall },
    { at: 66, lieId: "whisper", layer: "heart", label: "Fox on the wall", skin: N4.foxOnWall },
    { at: 74, lieId: "heavy-gloom", skin: N3.tooHeavy },
    { at: 82, lieId: "whisper", layer: "heart", skin: N4.theyMockYou },
    { at: 90, lieId: "whisper", skin: N4.weakHands },
    { at: 98, lieId: "heavy-gloom", layer: "heart", label: "Hold both", skin: N4.theyMockYou },
  ],
};

/** N5 — Spread / schemes (reuse Wildfire Season). */
const SCHEMES: ScenarioDef = {
  id: "neh-schemes",
  name: "Schemes and Letters",
  duration: 110,
  handSize: 5,
  maxIntegrity: 85,
  spawns: [
    { at: 3, lieId: "whisper", label: "Sparks", skin: N5.rumorSpreads },
    { at: 10, lieId: "wildfire", label: "Rumor spreads", skin: N5.rumorSpreads },
    { at: 18, lieId: "whisper", skin: N5.openLetter },
    { at: 26, lieId: "wildfire", skin: N5.rumorSpreads },
    { at: 34, lieId: "heavy-gloom", skin: N5.hideFlee },
    { at: 42, lieId: "wildfire", layer: "heart", label: "Open letter", skin: N5.openLetter },
    { at: 50, lieId: "whisper", skin: N5.comeDown },
    { at: 58, lieId: "wildfire", skin: N5.rumorSpreads },
    { at: 66, lieId: "alone", label: "Come down alone", skin: N5.comeDown },
    { at: 74, lieId: "wildfire", layer: "heart", skin: N5.openLetter },
    { at: 82, lieId: "whisper", skin: N5.hideFlee },
    { at: 90, lieId: "wildfire", skin: N5.rumorSpreads },
    { at: 98, lieId: "heavy-gloom", label: "Stay on the wall", skin: N5.hideFlee },
    { at: 104, lieId: "wildfire", skin: N5.rumorSpreads },
  ],
};

/** N6 — Will under fire (reuse Will Under Fire). */
const STRENGTH_DECAYED: ScenarioDef = {
  id: "neh-strength-decayed",
  name: "Strength Decayed",
  duration: 115,
  handSize: 5,
  maxIntegrity: 80,
  spawns: [
    { at: 3, lieId: "whisper", label: "Opening", skin: N6.strengthDecayed },
    { at: 10, lieId: "whisper", skin: N6.yourPeople },
    { at: 18, lieId: "heavy-gloom", layer: "heart", skin: N6.strengthDecayed },
    { at: 26, lieId: "wildfire", skin: N6.surpriseAttack },
    { at: 34, lieId: "whisper", layer: "heart", skin: N6.yourPeople },
    { at: 42, lieId: "alone", label: "Compounded", skin: N6.yourPeople },
    { at: 50, lieId: "heavy-gloom", layer: "will", label: "Strength decayed", skin: N6.strengthDecayed },
    { at: 58, lieId: "whisper", layer: "heart", skin: N6.muchRubbish },
    { at: 66, lieId: "wildfire", skin: N6.surpriseAttack },
    { at: 74, lieId: "alone", layer: "will", skin: N6.yourPeople },
    { at: 82, lieId: "anchor-weight", layer: "heart", label: "Much rubbish", skin: N6.muchRubbish },
    { at: 90, lieId: "whisper", layer: "will", skin: N6.strengthDecayed },
    { at: 98, lieId: "heavy-gloom", layer: "will", skin: N6.strengthDecayed },
    { at: 106, lieId: "wildfire", layer: "heart", label: "Hold Will", skin: N6.surpriseAttack },
  ],
};

/** N7 — Full mixed script (reuse Take Every Thought). */
const SWORD_AND_TROWEL: ScenarioDef = {
  id: "neh-sword-and-trowel",
  name: "Sword and Trowel",
  duration: 120,
  handSize: 5,
  maxIntegrity: 80,
  spawns: [
    { at: 2.5, lieId: "whisper", label: "First whispers", skin: N7.pickOne },
    { at: 8, lieId: "whisper", skin: N7.neverFinish },
    { at: 15, lieId: "heavy-gloom", label: "Gloom settles", skin: N7.neverFinish },
    { at: 22, lieId: "wildfire", skin: N5.rumorSpreads },
    { at: 28, lieId: "whisper", layer: "heart", label: "Bypass thought", skin: N4.theyMockYou },
    { at: 34, lieId: "whisper", skin: N7.pickOne },
    { at: 40, lieId: "alone", label: "Isolation", skin: N5.comeDown },
    { at: 46, lieId: "whisper", skin: N7.lastGap },
    { at: 52, lieId: "anchor-weight", layer: "heart", label: "Heart spike", skin: N6.muchRubbish },
    { at: 58, lieId: "wildfire", skin: N5.rumorSpreads },
    { at: 64, lieId: "heavy-gloom", skin: N7.neverFinish },
    { at: 70, lieId: "whisper", layer: "heart", skin: N4.foxOnWall },
    { at: 76, lieId: "alone", label: "Compounded", skin: N5.hideFlee },
    { at: 82, lieId: "wildfire", layer: "heart", skin: N5.openLetter },
    { at: 88, lieId: "anchor-weight", layer: "heart", skin: N7.lastGap },
    { at: 94, lieId: "heavy-gloom", layer: "will", label: "Will under fire", skin: N6.strengthDecayed },
    { at: 100, lieId: "whisper", layer: "heart", skin: N7.pickOne },
    { at: 106, lieId: "alone", layer: "will", skin: N6.yourPeople },
    { at: 111, lieId: "wildfire", skin: N5.rumorSpreads },
    { at: 116, lieId: "anchor-weight", label: "Finish the wall", skin: N7.lastGap },
  ],
};

/** N8 — Soft epilogue; Joy of the Lord payoff. */
const JOY_OF_THE_LORD: ScenarioDef = {
  id: "neh-joy-of-the-lord",
  name: "Joy of the Lord",
  duration: 70,
  handSize: 5,
  maxIntegrity: 140,
  disableConfusion: true,
  spawns: [
    { at: 6, lieId: "whisper", label: "Old echoes", skin: N8.oldEcho },
    { at: 20, lieId: "whisper", skin: N8.onlySolemn },
    { at: 34, lieId: "heavy-gloom", label: "Only solemnity", skin: N8.onlySolemn },
    { at: 48, lieId: "whisper", label: "Joy is strength", skin: N8.oldEcho },
    { at: 60, lieId: "whisper", skin: N8.onlySolemn },
  ],
};

/**
 * Rebuild the Wall — 8 stages (option B).
 * N1/N3–N7 reuse prior combat densities; N2 and N8 are new scripts.
 */
export const CAMPAIGN_MISSIONS: readonly MissionDef[] = [
  {
    id: "neh-before-the-king",
    index: 0,
    name: "Before the King",
    blurb:
      "Answer Fear on Mind. Also: drag Phil 4:6–7 onto an empty slot to plant a Guard Prayer — then mend it with 1 Thess 5:17 when lies wear it down.",
    story: STORY_BEFORE_THE_KING,
    scenario: BEFORE_THE_KING,
    rewardCards: ["ps-56-3", "rom-15-13", "mark-4-39"],
    loadout: {
      mode: "fixed",
      core: [
        "heb-11-1",
        "mark-11-22",
        "eph-6-16",
        "ps-46-10",
        "phil-4-6-7",
        "thess-5-17",
      ],
    },
  },
  {
    id: "neh-night-survey",
    index: 1,
    name: "The Night Survey",
    blurb:
      "Ride the ruin by night. Sparse pressure — be still, look honestly, answer without panic. Carry in what you earned.",
    story: STORY_NIGHT_SURVEY,
    scenario: NIGHT_SURVEY,
    rewardCards: ["isa-26-3", "2cor-10-5", "rom-10-17"],
    loadout: {
      mode: "fixed+carryIn",
      core: [
        "heb-11-1",
        "mark-11-22",
        "phil-4-6-7",
        "ps-46-10",
        "thess-5-17",
        "eph-6-16",
      ],
      carryInFromMissionId: "neh-before-the-king",
      maxCarryIn: 3,
    },
  },
  {
    id: "neh-rise-and-build",
    index: 2,
    name: "Rise and Build",
    blurb:
      "Let us rise up and build — under open scorn. Despair joins the assault. Peace and hope tools matter.",
    story: STORY_RISE_AND_BUILD,
    scenario: RISE_AND_BUILD,
    rewardCards: ["john-14-27", "col-3-15", "eph-6-17"],
    loadout: {
      mode: "fixed+carryIn",
      core: [
        "heb-11-1",
        "rom-15-13",
        "phil-4-6-7",
        "mark-4-39",
        "thess-5-17",
        "ps-46-10",
      ],
      carryInFromMissionId: "neh-night-survey",
      maxCarryIn: 3,
    },
  },
  {
    id: "neh-mockery",
    index: 3,
    name: "Mockery from Outside",
    blurb:
      "Insults aim at the Heart. Hold Mind and Heart — shield the fiery darts while the wall rises.",
    story: STORY_MOCKERY,
    scenario: MOCKERY,
    rewardCards: ["gen-50-20", "rom-8-28", "mark-11-23-24"],
    loadout: {
      mode: "fixed+carryIn",
      core: [
        "heb-11-1",
        "john-14-27",
        "col-3-15",
        "2cor-10-5",
        "phil-4-6-7",
        "mark-11-22",
      ],
      carryInFromMissionId: "neh-rise-and-build",
      maxCarryIn: 3,
    },
  },
  {
    id: "neh-schemes",
    index: 4,
    name: "Schemes and Letters",
    blurb:
      "False invitations and spreading rumor. Soften, Still, and board control — build from your collection.",
    story: STORY_SCHEMES,
    scenario: SCHEMES,
    rewardCards: ["mark-4-39", "gen-50-20", "rom-8-28"],
    loadout: {
      mode: "pool",
      core: [
        "heb-11-1",
        "john-14-27",
        "eph-6-16",
        "mark-4-39",
        "rom-10-17",
        "phil-4-6-7",
      ],
      minPoolSize: 6,
    },
  },
  {
    id: "neh-strength-decayed",
    index: 5,
    name: "Strength Decayed",
    blurb:
      "Weariness within and threat without. Pressure reaches the Will — renew strength and take refuge.",
    story: STORY_STRENGTH_DECAYED,
    scenario: STRENGTH_DECAYED,
    rewardCards: ["ps-91-1-2", "isa-40-31", "isa-26-3"],
    loadout: {
      mode: "pool",
      core: [
        "heb-11-1",
        "john-14-27",
        "rom-10-17",
        "rom-8-28",
        "ps-91-1-2",
        "phil-4-6-7",
      ],
      minPoolSize: 8,
    },
  },
  {
    id: "neh-sword-and-trowel",
    index: 6,
    name: "Sword and Trowel",
    blurb:
      "Build with one hand, hold the sword with the other. Full mixed pressure — finish the wall.",
    story: STORY_SWORD_AND_TROWEL,
    scenario: SWORD_AND_TROWEL,
    rewardCards: ["eph-6-17", "mark-11-23-24", "prov-3-6"],
    loadout: {
      mode: "pool",
      core: [
        "heb-11-1",
        "john-14-27",
        "eph-6-17",
        "gen-50-20",
        "ps-91-1-2",
        "mark-11-23-24",
      ],
      minPoolSize: 10,
    },
  },
  {
    id: "neh-joy-of-the-lord",
    index: 7,
    name: "Joy of the Lord",
    blurb:
      "Epilogue: the Law is read, tears turn to feast. Soft echoes only — the joy of the Lord is your strength.",
    story: STORY_JOY_OF_THE_LORD,
    scenario: JOY_OF_THE_LORD,
    // Signature payoff: Nehemiah 8:10 held until the end of the chapter
    rewardCards: ["neh-8-10", "rom-15-13", "isa-40-31"],
    loadout: {
      mode: "fixed",
      // Borrow Neh 8:10 so the signature verse is playable even before the grant
      core: [
        "neh-8-10",
        "rom-15-13",
        "phil-4-6-7",
        "isa-40-31",
        "heb-11-1",
        "thess-5-17",
      ],
    },
  },
];

/** All playable missions across campaigns (for rewards / lookup). */
export const ALL_MISSIONS: readonly MissionDef[] = [
  ...CAMPAIGN_MISSIONS,
  ...GIDEON_MISSIONS,
];

export function missionById(id: string): MissionDef | undefined {
  return ALL_MISSIONS.find((m) => m.id === id);
}

export function missionByIndex(index: number): MissionDef | undefined {
  return CAMPAIGN_MISSIONS.find((m) => m.index === index);
}

export { GIDEON_MISSIONS };
